/**
 * ruleText · 把后端规则数据拼成自然语言句子
 *
 * 与 photoFolders/Detail/index.jsx 的 ClassificationRule 行为对齐：
 *  · 三段：拍摄人 / 时间区间 / 关键词
 *  · 拼接末尾"照片将会自动添加"
 *  · 双引号包裹的部分用蓝色高亮（保持与老页面一致）
 *
 * 后端 rule 字段：
 *   { exist: boolean, keywords: string[], times: [{start, end}], nameList: [{name, number}] }
 *
 * 输出：JSX 元素，可直接 render；rule 不存在或为空 → null（调用方决定兜底文案）
 */

import React from 'react'

export interface ClassifyRule {
  exist?: boolean
  keywords?: string[]
  times?: Array<{ start?: string; end?: string }>
  nameList?: Array<{ name: string; number?: number }>
}

/** 是否构成"完整月份"区间（用于把 2026-03-01 ~ 2026-03-31 显示为"2026年3月"） */
function isFullMonth(startDateStr: string, endDateStr: string): boolean {
  const [sy, sm, sd] = startDateStr.split('-').map((n) => Number(n))
  const [ey, em, ed] = endDateStr.split('-').map((n) => Number(n))
  if (!(sy && sm && sd && ey && em && ed)) return false
  if (sy !== ey || sm !== em) return false
  if (sd !== 1) return false
  // new Date(y, m, 0).getDate() 拿当月最后一天
  const lastDay = new Date(sy, sm, 0).getDate()
  return ed === lastDay
}

/** 取 'YYYY-MM-DD HH:mm:ss' 等的前 10 位 */
function toDateStr(val: string | undefined): string {
  return val ? String(val).slice(0, 10) : ''
}

/**
 * 拼成纯文本（不带高亮 span），用于 aria-label / Tooltip 等场景
 */
export function buildRuleText(rule: ClassifyRule | null | undefined): string {
  if (!rule || !rule.exist) return ''
  const parts: string[] = []
  const { keywords = [], times = [], nameList = [] } = rule

  // 拍摄人
  if (nameList.length > 0) {
    if (nameList.length === 1) {
      parts.push(`"${nameList[0].name}"拍摄的`)
    } else {
      const totalPeople = nameList.reduce(
        (sum, item) => sum + (item.number || 0),
        0
      )
      const displayNames = nameList
        .slice(0, 3)
        .map((item) => item.name)
        .join('、')
      parts.push(`"${displayNames}"等${totalPeople}人拍摄的`)
    }
  }

  // 时间
  if (times.length > 0 && times[0].start) {
    const { start, end } = times[0]
    const startDateStr = toDateStr(start)
    const endDateStr = toDateStr(end)
    const isSameDay = !!end && startDateStr === endDateStr

    if (end) {
      if (isFullMonth(startDateStr, endDateStr)) {
        const [y, m] = startDateStr.split('-')
        parts.push(`"${y}年${Number(m)}月"的`)
      } else if (isSameDay) {
        parts.push(`"${startDateStr}"的`)
      } else {
        parts.push(`"${startDateStr}至${endDateStr}"时间段的`)
      }
    } else {
      parts.push(`"${startDateStr}至今"的`)
    }
  }

  // 关键词
  if (keywords.length > 0) {
    const keywordText = keywords.map((k) => `"${k}"`).join('、')
    parts.push(`包含${keywordText}的`)
  }

  if (parts.length === 0) return ''
  return parts.join('') + '照片将会自动添加'
}

/**
 * 把规则文案渲染成带蓝色高亮的 JSX —— 双引号内的关键信息高亮
 *
 * 规则不存在时返回 null，调用方决定如何兜底（通常显示淡灰 placeholder）
 */
export function renderRuleText(
  rule: ClassifyRule | null | undefined
): React.ReactNode {
  const text = buildRuleText(rule)
  if (!text) return null
  // 用 ".*?" 切分，被包裹的高亮，外面普通色
  const segments = text.split(/(".*?")/g)
  return segments.map((seg, idx) => {
    if (seg.startsWith('"') && seg.endsWith('"')) {
      return (
        <span key={idx} style={{ color: '#0070F4' }}>
          {seg}
        </span>
      )
    }
    return <React.Fragment key={idx}>{seg}</React.Fragment>
  })
}
